import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Estados compartidos para páginas bajo AdminLayout (black-glass).
 * Reemplaza los spinners/skeletons/empty-text sueltos para que carga y vacío
 * se vean iguales en todo el admin.
 *
 * For “wait while something loads” (panel / overlay / button): prefer
 * `AdminWait` + `AdminWaitSurface` from `@/components/admin/AdminWait`.
 */

export { AdminWait, AdminWaitSurface } from "@/components/admin/AdminWait";
export type { AdminWaitProps, AdminWaitVariant, AdminWaitSize } from "@/components/admin/AdminWait";

export function AdminSectionSkeleton() {
  return (
    <div className="space-y-5 animate-pulse" aria-busy="true" aria-live="polite">
      <div className="h-7 w-52 max-w-full rounded-lg bg-white/[0.06]" />
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-[5.5rem] rounded-xl bg-white/[0.04] ring-1 ring-inset ring-white/[0.06]"
          />
        ))}
      </div>
      <div className="h-56 rounded-xl bg-white/[0.03] ring-1 ring-inset ring-white/[0.05]" />
    </div>
  );
}

type AdminLoadingStateProps = {
  /** Texto bajo el spinner. */
  label?: string;
  className?: string;
  /** Si true: ocupa toda la altura del contenedor (ideal para dentro del AdminLayout). */
  full?: boolean;
  /** Tamaño más compacto (para chips o áreas pequeñas). */
  compact?: boolean;
};

export function AdminLoadingState({
  label = "Cargando…",
  className,
  full = false,
  compact = false,
}: AdminLoadingStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 text-center",
        // Inline (inside a page card): glass panel
        !full &&
          !compact &&
          "admin-material-panel rounded-xl px-6 py-14",
        // Fill remaining shell — solid black, never half-viewport white cut
        full &&
          "min-h-[calc(100svh-3.5rem)] w-full bg-black px-6 py-16 sm:min-h-[calc(100svh-3rem)]",
        compact && "gap-2 px-4 py-6",
        className,
      )}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <Loader2
        className={cn(
          "animate-spin text-white/50",
          compact ? "h-4 w-4" : "h-5 w-5",
        )}
        aria-hidden
      />
      <p
        className={cn(
          "text-white/45",
          compact ? "text-[12px]" : "text-[13px]",
        )}
      >
        {label}
      </p>
    </div>
  );
}

/**
 * Loading a pantalla completa (antes de AdminLayout / ProtectedAdminRoute).
 * Negro total — evita el “corte a la mitad” con fondo claro del body.
 */
export function AdminRouteLoading({ label = "Cargando…" }: { label?: string }) {
  return (
    <div
      className="fixed inset-0 z-[100] flex min-h-svh w-full items-center justify-center bg-black text-white/60"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="h-5 w-5 animate-spin text-white/40" aria-hidden />
        <span className="text-[13px] tracking-wide text-white/50">{label}</span>
      </div>
    </div>
  );
}

/**
 * Loading full-bleed reutilizable (admin + portal).
 * Mismo look negro que AdminRouteLoading.
 */
export function FullBleedDarkLoading({
  label = "Cargando…",
}: {
  label?: string;
}) {
  return <AdminRouteLoading label={label} />;
}

/**
 * AdminSkeleton
 * Dark-glass friendly skeleton (replaces the generic ui/Skeleton which uses bg-muted).
 * Use for placeholder blocks inside admin pages.
 */
export function AdminSkeleton({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "animate-pulse rounded-md bg-white/[0.06]",
        className,
      )}
      aria-hidden="true"
      {...props}
    />
  );
}

type AdminEmptyStateProps = {
  /** Título corto del estado vacío. */
  title: string;
  /** Descripción opcional. */
  description?: ReactNode;
  /** Icono opcional (lucide). */
  icon?: ReactNode;
  /** Acción principal (botón). */
  action?: ReactNode;
  /** Acción secundaria (link / botón ghost). */
  secondaryAction?: ReactNode;
  /** Visual style. */
  variant?: "default" | "subtle";
  className?: string;
};

export function AdminEmptyState({
  title,
  description,
  icon,
  action,
  secondaryAction,
  variant = "default",
  className,
}: AdminEmptyStateProps) {
  return (
    <div
      className={cn(
        "admin-material-panel flex flex-col items-center justify-center gap-3 rounded-xl px-6 py-12 text-center sm:py-14",
        variant === "default" ? "border-dashed" : "",
        className,
      )}
    >
      {icon ? (
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/[0.05] text-white/50 ring-1 ring-inset ring-white/[0.08] [&_svg]:h-5 [&_svg]:w-5">
          {icon}
        </div>
      ) : null}
      <div className="space-y-1.5">
        <p className="text-[15px] font-medium tracking-tight text-white/85">{title}</p>
        {description ? (
          <p className="admin-type-caption mx-auto max-w-sm text-[13px] text-white/40">
            {description}
          </p>
        ) : null}
      </div>
      {(action || secondaryAction) && (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          {action}
          {secondaryAction}
        </div>
      )}
    </div>
  );
}

/**
 * AdminErrorState
 * Consistent error presentation inside admin glass surfaces.
 */
export interface AdminErrorStateProps {
  title?: string;
  description?: ReactNode;
  /** Retry action (button). */
  retry?: () => void;
  /** Label for retry button. */
  retryLabel?: string;
  /** Extra actions. */
  action?: ReactNode;
  className?: string;
  /** Compact for inline / card use. */
  compact?: boolean;
}

/**
 * Banner de error estándar bajo el header de cada página admin.
 * Reemplaza Alert variant="destructive" sueltos.
 */
export function AdminPageErrorBanner({
  error,
  onRetry,
  className,
}: {
  error: string | null | undefined;
  onRetry?: () => void;
  className?: string;
}) {
  if (!error) return null;
  return (
    <AdminErrorState
      compact
      description={error}
      retry={onRetry}
      className={className}
    />
  );
}

export function AdminErrorState({
  title = "Error al cargar",
  description = "No pudimos obtener los datos. Intentá de nuevo.",
  retry,
  retryLabel = "Reintentar",
  action,
  className,
  compact = false,
}: AdminErrorStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 rounded-xl border border-red-500/25 bg-red-500/[0.05] px-6 text-center shadow-[inset_0_1px_0_rgb(255_255_255/0.04)]",
        compact ? "py-6" : "py-10 sm:py-12",
        className,
      )}
      role="alert"
    >
      <p className="text-[14px] font-medium text-red-300/90">{title}</p>
      {description && (
        <p className="max-w-sm text-[12.5px] leading-relaxed text-red-200/70">
          {description}
        </p>
      )}
      <div className="mt-1 flex flex-wrap gap-2">
        {retry && (
          <button
            type="button"
            onClick={retry}
            className="rounded-md border border-white/15 bg-white/5 px-3 py-1 text-[12px] text-white/80 transition hover:bg-white/10 hover:text-white"
          >
            {retryLabel}
          </button>
        )}
        {action}
      </div>
    </div>
  );
}
